import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Building2, Loader2, Check, X, RefreshCw, Mail, Phone, MapPin, CreditCard, FileText,
} from "lucide-react";

const STATUSES = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]];

export const CorporateApplicationsPanel = () => {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await api.get(`/admin/corporate/applications?status=${status}`); setData(res.data); }
    catch (e) { toast.error(formatApiError(e) || "Failed to load applications"); }
  }, [status]);
  useEffect(() => { setData(null); load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Approve this application? This creates a company + a corporate-admin login and emails them a set-password link.")) return;
    setBusy(true);
    try { await api.post(`/admin/corporate/applications/${id}/approve`); toast.success("Approved — welcome email sent."); setOpen(null); await load(); }
    catch (e) { toast.error(formatApiError(e) || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (the applicant will see this):");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/corporate/applications/${id}/reject`, { reason: reason.trim() }); toast.success("Rejected — applicant notified."); setOpen(null); await load(); }
    catch (e) { toast.error(formatApiError(e) || "Reject failed"); }
    finally { setBusy(false); }
  };

  const saveNotes = async (id, notes) => {
    try { await api.put(`/admin/corporate/applications/${id}/notes`, { internal_notes: notes }); toast.success("Notes saved"); }
    catch (e) { toast.error(formatApiError(e) || "Could not save notes"); }
  };

  const apps = data?.applications || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-4" data-testid="corporate-applications-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#002FA7]" />
          <h2 className="font-heading text-2xl font-black tracking-tight">Corporate Applications</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setData(null); load(); }} data-testid="corp-apps-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="flex gap-px bg-border inline-flex" data-testid="corp-apps-status-tabs">
        {STATUSES.map(([id, label]) => (
          <button key={id} onClick={() => setStatus(id)} className={`px-4 py-2 text-sm font-bold ${status === id ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`} data-testid={`corp-apps-tab-${id}`}>
            {label} {counts[id] != null ? <span className="opacity-70">({counts[id]})</span> : null}
          </button>
        ))}
      </div>

      {data === null ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : apps.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground border border-dashed border-border" data-testid="corp-apps-empty">No {status} applications.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="corp-apps-list">
          {apps.map((a) => (
            <div key={a.id} className="border border-border bg-white p-4" data-testid={`corp-app-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-black text-lg truncate">{a.company_name}</div>
                  {a.trading_name && <div className="text-xs text-muted-foreground">t/a {a.trading_name}</div>}
                </div>
                <span className={`label-mini px-1.5 py-0.5 border whitespace-nowrap ${a.status === "approved" ? "border-[#10B981] text-[#065F46] bg-[#ECFDF5]" : a.status === "rejected" ? "border-[#EF4444] text-[#991B1B] bg-[#FEF2F2]" : "border-[#F59E0B] text-[#92400E] bg-[#FFFBEB]"}`}>{a.status.toUpperCase()}</span>
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                <Row icon={Mail}>{a.contact_name} · {a.email}</Row>
                <Row icon={Phone}>{a.phone}</Row>
                {a.address && <Row icon={MapPin}>{a.address}</Row>}
                <Row icon={CreditCard}>{(a.payment_preference || "").replace("_", " ")} · {a.est_monthly_trips ?? "?"} trips/mo · £{a.est_monthly_spend ?? "?"}/mo</Row>
                {a.notes && <Row icon={FileText}>{a.notes}</Row>}
              </div>

              <div className="mt-3 pt-3 border-t border-border">
                <textarea defaultValue={a.internal_notes || ""} placeholder="Internal notes (not shown to applicant)…" rows={2}
                  onBlur={(e) => { if (e.target.value !== (a.internal_notes || "")) saveNotes(a.id, e.target.value); }}
                  className="w-full border border-border px-2 py-1.5 text-xs resize-none focus:outline-none focus:border-[#0A0A0A]" data-testid={`corp-app-notes-${a.id}`} />
              </div>

              {a.status === "pending" && (
                <div className="mt-3 flex gap-2">
                  <Button onClick={() => approve(a.id)} disabled={busy} className="flex-1 bg-[#10B981] hover:bg-[#0E9F6E] text-white flex items-center justify-center gap-1.5" data-testid={`corp-app-approve-${a.id}`}><Check className="w-4 h-4" /> Approve</Button>
                  <Button onClick={() => reject(a.id)} disabled={busy} variant="outline" className="flex-1 text-[#991B1B] border-[#FCA5A5] hover:bg-[#FEF2F2] flex items-center justify-center gap-1.5" data-testid={`corp-app-reject-${a.id}`}><X className="w-4 h-4" /> Reject</Button>
                </div>
              )}
              {a.status === "rejected" && a.rejection_reason && (
                <div className="mt-3 text-xs text-[#991B1B] bg-[#FEF2F2] border border-[#FCA5A5] p-2" data-testid={`corp-app-reason-${a.id}`}><strong>Reason:</strong> {a.rejection_reason}</div>
              )}
              {a.status === "approved" && (
                <div className="mt-3 text-xs text-[#065F46] bg-[#ECFDF5] border border-[#10B981] p-2">Company + corporate-admin login created.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2 text-muted-foreground"><Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /><span className="text-foreground">{children}</span></div>
);

export default CorporateApplicationsPanel;
